import { BasicCartesianChartConfig, AnalyteConfig } from '@app/data/interface/widget-config';
import {
  BasicCartesianChartEchartConfigTemplate,
  BasicCartesianChartAxisTemplate,
} from '@app/data/widget-config-templates/basic-cartesian-chart';

const analytesLandUseImpact = [
  // { analyte: 'turbidity', name: 'Turbidity', unit: 'NTU', default: false },
  // { analyte: 'turbidity-field', name: 'Turbidity (field)', unit: 'NTU', default: false },
  // { analyte: 'ph-in-lab', name: 'pH (lab)', unit: '', default: false },
  // { analyte: 'ph-in-field', name: 'pH (field)', unit: '', default: false },
  // { analyte: 'electrical-conductivity-in-field', name: 'Electrical Conductivity', unit: 'mS/m', default: true },
  // { analyte: 'total-suspended-solids', name: 'Total Suspended Solids', unit: 'g/m³', default: false },
  // { analyte: 'chloride', name: 'Chloride', unit: 'g/m³', default: false },
  // { analyte: 'total-ammoniacal-nitrogen', name: 'Total Ammoniacal-N', unit: 'g/m³', default: false },
  // { analyte: 'nitrite-n', name: 'Nitrite-N', unit: 'g/m³' },
  // { analyte: 'nitrate-n', name: 'Nitrate-N', unit: 'g/m³' },
  // { analyte: 'nitrate-+-nitrite-nitrogen', name: 'Nitrate-N + Nitrite-N', unit: 'g/m³' } as AnalyteConfig,
  {
    analyte: 'total-phosphorus',
    name: 'Total Phosphorus',
    unit: 'g/m³',
    default: true,
    color: '#8c7ebb',
  } as AnalyteConfig,
  { analyte: 'Total Nitrogen', name: 'Total Nitrogen', unit: 'g/m³', default: true, color: '#66c39b' } as AnalyteConfig,
  // { analyte: 'biochemical-oxygen-on-demand', name: 'BOD as O2', unit: 'g/m³', default: false },
  // { analyte: 'chemical-oxygen-demand', name: 'COD as O2', unit: 'g/m³', default: false },
  // { analyte: 'dissolved-oxygen-saturation-in-field', name: 'DO-field', unit: '%', default: false },
  // { analyte: 'total-alkalinity', name: 'Alkalinity', unit: 'g/m³', default: false },
];

const analytesECOSystemHealth = [
  { analyte: 'relative-water-level', name: 'Water Level', unit: 'm', default: true, color: '#007093' } as AnalyteConfig,
  { analyte: 'turbidity', name: 'Turbidity', unit: 'NTU', default: true, color: '#26312b' } as AnalyteConfig,
  { analyte: 'Total Nitrogen', name: 'Total Nitrogen', unit: 'g/m³', default: true, color: '#66c39b' } as AnalyteConfig,
  {
    analyte: 'dissolved-reactive-phosphorous',
    name: 'Dissolved Reactive Phosphorus',
    unit: 'g/m³',
    default: true,
    color: '#8c7ebb',
  } as AnalyteConfig,
];

const analytesWai = [
  { analyte: 'ph-in-field', name: 'pH (field)', unit: '', default: true, color: '#007093' } as AnalyteConfig,
  {
    analyte: 'dissolved-reactive-phosphorous',
    name: 'Dissolved Reactive Phosphorus',
    unit: 'g/m³',
    default: true,
    color: '#8c7ebb',
  } as AnalyteConfig,
  {
    analyte: 'total-ammoniacal-nitrogen',
    name: 'Total Ammoniacal Nitrogen',
    unit: 'g/m³',
    default: true,
    color: '#00b1d8',
  } as AnalyteConfig,
  { analyte: 'turbidity', name: 'Turbidity', unit: 'NTU', default: true, color: '#26312b' } as AnalyteConfig,
];

function ConfigureChart(
  title: string,
  environmentalDataSourceIdentifier: string,
  analytes: AnalyteConfig[]
): BasicCartesianChartConfig {
  // group analytes by unit
  const analytesByUnit = analytes.reduce((acc, curr) => {
    const unit = curr.unit || 'no unit';
    const existingUnit = acc.find((item) => item.unit === unit);
    if (existingUnit) {
      existingUnit.analytes.push(curr);
    } else {
      acc.push({ unit, analytes: [curr] });
    }
    return acc;
  }, [] as Array<{ unit: string; analytes: (typeof analytes)[0][] }>);

  return {
    echartsConfig: {
      ...BasicCartesianChartEchartConfigTemplate,
      color: analytes.map((a) => (a.color ? a.color : undefined)).filter((c) => c !== undefined) as string[],
      grid: {
        top: '30%',
        left: '11%',
        right: '11%',
        bottom: '6%',
        containLabel: true,
      },
      legend: {
        padding: [10, 10, 10, 10], // top, right, bottom, left
        show: true,
        data: analytes.map((aa) => aa.name),
        selected: analytes.reduce((acc, a) => {
          acc[a.name] = a.default;
          return acc;
        }, {} as Record<string, boolean>),
      },
      yAxis: analytesByUnit.map((entry, index) => {
        return {
          ...BasicCartesianChartAxisTemplate,
          type: 'value',
          position: index % 2 === 0 ? 'left' : 'right',
          offset: 40 * (index / 2),
          name: entry.unit,
          axisLine: {
            show: true,
            lineStyle: {
              color: entry.analytes[0].color,
            },
          },
          axisLabel: {
            color: entry.analytes[0].color,
          },
        };
      }),
      series: analytesByUnit.reduce((acc, curr, i) => {
        const s = curr.analytes.map((entry, index) => ({
          type: 'line',
          name: entry.name,
          smooth: true,
          symbolSize: 13,
          yAxisIndex: i,
          showSymbol: true,
          connectNulls: true,
        })) as [];
        return [...acc, ...s];
      }, []),
    },
    dataConfig: {
      title: title,
      widgetId: 1,
      domain: 'wai',
      dataSources: analytes.map((entry) => ({
        environmentalDataSourceIdentifier: environmentalDataSourceIdentifier,
        analyteIdentifier: entry.analyte,
        valueField: 'avg',
        label: entry.name,
      })),
    },
  };
}

export const WhakaaweWaipaRiverNgaruawahia = ConfigureChart(
  'WHAKAAWE WHENUA - Land Use Impact: Mangakarengorengo River (Ngamuwahine at Old Bridge)',
  'boprc_ngamuwahine_at_old_bridge',
  analytesLandUseImpact
);

export const WhakaaweKarapiroStream = ConfigureChart(
  'WHAKAAWE WHENUA - Land Use Impact: Karapiro Stm - Hickey Rd Bridge',
  'wrc_230_5',
  analytesLandUseImpact
);

export const WhakaaweKMangaharakekeStream = ConfigureChart(
  'WHAKAAWE WHENUA - Land Use Impact: Mangaharakeke Stm (Atiamuri)',
  'wrc_359_1',
  analytesLandUseImpact
);

export const EcoKMangaharakekeStream = ConfigureChart(
  'TAIAO - Ecosystem Health: Mangaharakeke Stm (Atiamuri)',
  'wrc_359_1',
  analytesECOSystemHealth
);

export const WaiKWaihouRiver = ConfigureChart(
  'Analyte WAI - Field Water Sampling: Waihou River',
  'wrc_1122_18',
  analytesWai
);

export const EcoKWaihouRiver = ConfigureChart(
  'TAIAO - Ecosystem Health: Waihou River',
  'wrc_1122_18',
  analytesECOSystemHealth
);
